import React from "react";
import { Box } from "@mui/joy";
import { ExpandMoreIcon, ExpandLessIcon } from "../../../styles/StyledMUI";
import DarkCard from "../../common/DarkCard";
import DarkTypography from "../../common/DarkTypography";
import TrainingDocs from "../display/TrainingDocs";
import TrainingTest from "../display/TrainingTest";
import FollowUpComponent from "../display/FollowUpComponent";
import FileWithPDFComponent from "../display/FileWithPDFComponent";
import type { PortalResponse } from "../../../api/portal/Portal.typed";
import type { Training } from "../../../api/training/Training.typed";
import IconButtonCTA from "../../common/IconButtonCTA";

type TrainingSectionProps = {
  title: string;
  show: boolean;
  children: React.ReactNode;
};

function TrainingSection({ title, show, children }: TrainingSectionProps) {
  if (!show) return null;
  return (
    <Box>
      <DarkTypography level="h4" fontSize="16px" colorVariant="accent">
        {title}
      </DarkTypography>
      {children}
    </Box>
  );
}

type TrainingsListProps = {
  customer?: PortalResponse;
  trainings: Training[];
  openTrainings: Record<number, boolean>;
  toggleTrainingOpen: (idx?: number, trainingTemplateId?: number) => void;
  docsByTemplateId: Record<
    number,
    {
      TrainingCourses?: { ID: string; Name: string; Url: string }[];
      MedicalAgreement?: string;
      MedicalDisclaimer?: string;
    }
  >;
  testAvailability: Record<number, boolean>;
  disclaimer: File | string | undefined;
  setDisclaimer: React.Dispatch<React.SetStateAction<File | string | undefined>>;
  agreement: File | string | undefined;
  setAgreement: React.Dispatch<React.SetStateAction<File | string | undefined>>;
};

export default function TrainingsList(props: TrainingsListProps) {
  return (
    <Box margin={1}>
      <DarkCard dense>
        <DarkTypography level="h4" fontSize="16px" colorVariant="accent">
          Mes formations
        </DarkTypography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {props.trainings.map((t, idx) => {
            const editable = t.Status === "En cours" || t.Status === "Terminé";
            const statusEmoji =
              t.Status === "Annulé"
                ? "❌"
                : t.Status === "Validé"
                  ? "✅"
                  : t.Status === "En cours"
                    ? "🟡"
                    : t.Status === "Terminé"
                      ? "🟢"
                      : "ℹ️";
            const open = !!props.openTrainings[idx];
            return (
              <DarkCard key={`card-${idx}`} dense sx={{ p: 1.5 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <DarkTypography level="h4" fontSize="14px" colorVariant="secondary">
                      {t.Agency || "Formation"}
                    </DarkTypography>
                    <DarkTypography level="body-sm" fontSize="12px" colorVariant="muted">
                      {t.Qualification}
                    </DarkTypography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DarkTypography level="body-sm" fontSize="12px" colorVariant="secondary">
                      {statusEmoji} {t.Status || "N/A"}
                    </DarkTypography>
                    <IconButtonCTA
                      size="sm"
                      ctaVariant="accent"
                      aria-label={open ? "Replier la formation" : "Deplier la formation"}
                      onClick={() => props.toggleTrainingOpen(idx, t.TrainingTemplateID)}
                    >
                      {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButtonCTA>
                  </Box>
                </Box>
                {open && (
                  <Box>
                    {editable ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        {(() => {
                          const docs = t.TrainingTemplateID
                            ? props.docsByTemplateId[t.TrainingTemplateID]
                            : undefined;
                          return (
                            <TrainingSection
                              title="Documents"
                              show={!!(docs?.TrainingCourses && docs.TrainingCourses.length > 0)}
                            >
                              <TrainingDocs courses={docs?.TrainingCourses} />
                            </TrainingSection>
                          );
                        })()}
                        <TrainingSection
                          title="Test en ligne"
                          show={
                            !!(t.TrainingTemplateID && props.testAvailability[t.TrainingTemplateID])
                          }
                        >
                          <TrainingTest
                            trainingTemplateId={t.TrainingTemplateID}
                            theoryTests={t.TheoryTests}
                          />
                        </TrainingSection>
                        <TrainingSection
                          title="Fiche de suivi"
                          show={!!(t.FollowUpSheet || (t.Dives && t.Dives.length > 0))}
                        >
                          <FollowUpComponent
                            customerId={props.customer?.Id}
                            trainingTemplateId={t.TrainingTemplateID}
                            followUpSheet={t.FollowUpSheet}
                            dives={t.Dives}
                          />
                        </TrainingSection>
                        <TrainingSection title="Décharge de responsabilité SDI" show={!!t.IsSDI}>
                          <FileWithPDFComponent
                            file={props.disclaimer || t.MedicalDisclaimer}
                            setFile={props.setDisclaimer}
                            pdf={
                              t.TrainingTemplateID
                                ? props.docsByTemplateId[t.TrainingTemplateID]?.MedicalDisclaimer
                                : undefined
                            }
                          />
                        </TrainingSection>
                        <TrainingSection title="Décharge médicale SDI" show={!!t.IsSDI}>
                          <FileWithPDFComponent
                            file={props.agreement || t.MedicalAgreement}
                            setFile={props.setAgreement}
                            pdf={
                              t.TrainingTemplateID
                                ? props.docsByTemplateId[t.TrainingTemplateID]?.MedicalAgreement
                                : undefined
                            }
                          />
                        </TrainingSection>
                      </Box>
                    ) : (
                      <DarkTypography
                        level="h4"
                        fontSize="14px"
                        colorVariant="secondary"
                        sx={{ mt: 1 }}
                      >
                        Cette formation n'est plus modifiable.
                      </DarkTypography>
                    )}
                  </Box>
                )}
              </DarkCard>
            );
          })}
        </Box>
      </DarkCard>
    </Box>
  );
}
